/*
  Copyright 2018-2020 National Geographic Society

  Use of this software does not constitute endorsement by National Geographic
  Society (NGS). The NGS name and NGS logo may not be used for any purpose without
  written permission from NGS.

  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import createAuth0Client, { Auth0Client } from '@auth0/auth0-spa-js';
import get from 'lodash/get';
import qs from 'query-string';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import {
  getPrivateGroups,
  getPublicGroups,
  hasAccess,
  isAuthz,
  mapAuthzScopes,
  mapRoleGroups,
  SessionStorage,
} from '@marapp/earth-shared';

import auth0 from '../config/auth0';
import { routeToPage } from '../utils';
import { Auth0Context } from '../utils/contexts';
import ProfileService from '../services/ProfileService'; // <-- Add this import

// Auth0 will enforce namespacing when performing OIDC-conformant
// login flows, meaning that any custom claims without HTTP/HTTPS
// namespaces will be silently excluded from tokens.
const NAMESPACE = auth0.config.namespace;
const PERSISTED_GROUPS_KEY = 'ephemeral-groups';
const TOKEN_EXPIRY_WARNING_MS = 5 * 60 * 1000; // warn 5 minutes before expiry

/**
 * Decode a JWT payload to extract claims (e.g. exp).
 * Does not validate the signature — that's the server's job.
 */
function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const base64 = token.split('.')[1];
    const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export const useAuth0 = () => useContext(Auth0Context);

interface Auth0ProviderOptions {
  domain?: any;
  client_id?: any;
  redirect_uri?: any;
  audience?: any;
  children: React.ReactElement;
  useRefreshTokens?: boolean;
  cacheLocation?: 'memory' | 'localstorage';
  onRedirectCallback(targetUrl: any): void;
  onSuccessHook(params: any): void;
  onFailureHook(params: any): void;
}

interface IQueryParams {
  org?: string;
  error?: string;
  code?: string;
}

interface UserData {
  email: string;
  nickname: string;
  name: string;
  picture: string;
  country: string;
  allGroups: string[];
  roles: any; // You may want to specify a more precise type if possible
  organization: string;
}

export const Auth0Provider = ({
  children,
  onRedirectCallback,
  onSuccessHook,
  onFailureHook,
  ...initOptions
}: Auth0ProviderOptions & Auth0Client) => {
  const [client, setClient] = useState<any>({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [email, setEmail] = useState('');
  const [userData, setUserData] = useState<UserData>({
    email: '',
    nickname: '',
    name: '',
    picture: '',
    country: '',
    allGroups: [],
    roles: {},
    organization: '',
  });

  // const [userProfile, setUserProfile] = useState({
  //   firstName: '',
  //   lastName: '',
  //   country: '',
  //   institution: '',
  //   groups: [],
  // });

  const [groups, setGroups] = useState([]);
  const [publicGroups, setPublicGroups] = useState([]);
  const [privateGroups, setPrivateGroups] = useState([]);
  const [roles, setRoles] = useState({});
  const [permissions, setPermissions] = useState({});

  const [tokenExpiringSoon, setTokenExpiringSoon] = useState(false);
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Schedule a warning before the JWT access token expires.
   * Clears any previously scheduled timer.
   */
  const scheduleExpiryWarning = useCallback((accessToken: string) => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    const payload = decodeJwtPayload(accessToken);
    if (!payload?.exp) return;

    const msUntilExpiry = payload.exp * 1000 - Date.now();
    const msUntilWarning = msUntilExpiry - TOKEN_EXPIRY_WARNING_MS;

    if (msUntilWarning <= 0) {
      // Already within the warning window
      setTokenExpiringSoon(true);
      return;
    }

    setTokenExpiringSoon(false);
    expiryTimerRef.current = setTimeout(() => {
      setTokenExpiringSoon(true);
    }, msUntilWarning);
  }, []);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    };
  }, []);

  // Update selectedWorkspace to use array format
  const [selectedWorkspace, setSelectedWorkspaceState] = useState<string[]>([]);
  const [availableWorkspaces, setAvailableWorkspaces] = useState<string[]>([]);

  // Keep selectedGroup as a mirror of selectedWorkspace for backward compatibility
  const [selectedGroup, setSelectedGroupState] = useState<string[]>([]);

  // Update selectedGroup when selectedWorkspace changes
  useEffect(() => {
    if (selectedWorkspace && selectedWorkspace.length > 0) {
      const groupArray = [...selectedWorkspace];
      if (!selectedWorkspace.includes('common')) {
        groupArray.push('common');
      }
      setSelectedGroupState(groupArray);
    } else {
      setSelectedGroupState([]);
    }
  }, [selectedWorkspace]);

  useEffect(() => {
    const initAuth0 = async () => {
      const auth0FromHook = await createAuth0Client(initOptions as any);
      const queryParams: IQueryParams = qs.parse(window.location.search);
      setClient(auth0FromHook);

      if (queryParams.code) {
        try {
          const { appState } = await auth0FromHook.handleRedirectCallback();
          onRedirectCallback({ targetUrl: appState.targetUrl });
        } catch (e) {
          // since we don't support IdP-Initiated Single Sign-On,
          // redirect to root page;
          // @ts-ignore
          console.error(e);
          routeToPage({});
        }
      }

      if (queryParams.error) {
        onFailureHook(queryParams);
      }

      const isAuthenticated = await auth0FromHook.isAuthenticated();
      setIsAuthenticated(isAuthenticated);

      const accessToken = isAuthenticated ? await auth0FromHook.getTokenSilently() : null;
      if (accessToken) scheduleExpiryWarning(accessToken);
      onSuccessHook({ accessToken, authClient: auth0FromHook });

      const idToken = accessToken ? await auth0FromHook.getUser() : {};

      const roles = get(idToken, `${NAMESPACE}/roles`, []);
      const mappedRoles = mapAuthzScopes(roles);
      setRoles(mappedRoles);

      setPrivateGroups(getPrivateGroups(mappedRoles));
      setPublicGroups(getPublicGroups(mappedRoles));

      const roleGroups = mapRoleGroups(roles, ['*']); // exclude special groups;
      setGroups(roleGroups);

      const permissions = get(idToken, `${NAMESPACE}/permissions`, []);
      setPermissions(mapAuthzScopes(permissions));

      const emailVerified = get(idToken, 'email_verified', false);
      setIsEmailVerified(emailVerified);

      const authorized = isAuthz(roles);
      setIsAuthorized(authorized);

      // Fetch user profile from ProfileService after authentication
      let fetchedUserProfile = {
        firstName: '',
        lastName: '',
        country: '',
        institution: '',
        groups: [],
      };
      if (isAuthenticated) {
        try {
          const response = await ProfileService.fetchProfile();
          fetchedUserProfile = response.data;
          // console.log('Auth0 init: fetchedUserProfile:', fetchedUserProfile);
          // setUserProfile(fetchedUserProfile);
          // console.log('Auth0 init: userProfile:', userProfile);
        } catch (err) {
          // Optionally handle error
          console.error('Failed to fetch user profile:', err);
        }
      }

      const givenName = get(idToken, 'given_name', '');
      const familyName = get(idToken, 'family_name', '');
      const nickname = get(idToken, 'nickname', '');

      const organization =
        fetchedUserProfile.institution || roleGroups.filter((g) => g !== 'common')[0];

      const userData = {
        email: get(idToken, 'email', ''),
        nickname: nickname,
        name:
          fetchedUserProfile.firstName && fetchedUserProfile.lastName
            ? `${fetchedUserProfile.firstName} ${fetchedUserProfile.lastName}`.trim()
            : `${givenName} ${familyName}`.trim(),
        picture: get(idToken, 'picture', ''),
        country: fetchedUserProfile.country || 'Earth',
        allGroups: roleGroups,
        roles: mappedRoles,
        organization: organization,
      };
      setUserData(userData);
      setEmail(userData.email);
      // console.log('Auth0 init: userData:', userData);

      // Set initial workspace
      if (organization) {
        setSelectedWorkspaceState([organization]); // ✅ Use array format
      }

      // Set available workspaces
      setAvailableWorkspaces(roleGroups);

      setIsLoading(false);
    };
    initAuth0();
  }, []); // eslint-disable-line

  // Update userData when selectedWorkspace changes
  useEffect(() => {
    if (!isLoading && Object.keys(userData).length > 0 && selectedWorkspace) {
      console.log('Auth0 switch org from:', userData.organization, ' to:', selectedWorkspace);
      //console.log('Auth0 to new org:', selectedWorkspace);
      setUserData((prevUserData) => ({
        ...prevUserData,
        organization: selectedWorkspace[0] || userData.organization,
      }));
    }
  }, [selectedWorkspace, isLoading]);

  // // Update userData when userProfile changes
  // useEffect(() => {
  //   if (!isLoading && Object.keys(userData).length > 0) {
  //     const organization = selectedWorkspace[0] || userProfile.institution;

  //     setUserData((prevUserData) => ({
  //       ...prevUserData,
  //       organization: organization,
  //       name: `${userProfile.firstName} ${userProfile.lastName}`.trim(),
  //       country: userProfile.country,
  //     }));

  //     // Only set workspace to institution if no workspace is currently selected
  //     if (!selectedWorkspace.length && userProfile.institution) {
  //       setSelectedWorkspaceState([userProfile.institution]); // Only set if no workspace selected
  //     }
  //   }
  // }, [userProfile, isLoading]);

  /**
   * Logging-in will automatically request the offline_access scope and store the resulting
   * refresh token.
   * @param options
   */
  const login = (options = {}) => {
    SessionStorage.remove('ephemeral');
    SessionStorage.remove(PERSISTED_GROUPS_KEY);

    return client.loginWithRedirect(options);
  };

  const logout = (options = {}) => {
    SessionStorage.remove('ephemeral');
    SessionStorage.remove(PERSISTED_GROUPS_KEY);

    // force the user to log out of their identity provider;
    return client.logout({ ...options, federated: true });
  };

  /**
   * Silently refreshing the access token will use the /token endpoint with ‘refresh_token’
   * grant and the refresh token from the cache.
   * @param options
   */
  const getAccessToken = (options = {}): Promise<string> => {
    return client.getTokenSilently(options);
  };

  const getUser = (options = {}) => {
    return client.getUser(options);
  };

  const updateToken = async () => {
    const accessToken = isAuthenticated ? await getAccessToken({ ignoreCache: true }) : null;

    if (accessToken) {
      scheduleExpiryWarning(accessToken);
    }
    onSuccessHook({ accessToken });
  };

  const getPermissions = (type: string[], org: string) => {
    return hasAccess(permissions[org], type) || hasAccess(permissions['*'], type);
  };

  // Add logging in the provider value return:
  return (
    <Auth0Context.Provider
      value={{
        isLoading,
        isAuthenticated,
        isAuthorized,
        isEmailVerified,
        email,
        userData,
        // userProfile,
        // setUserProfile,
        groups,
        publicGroups,
        privateGroups,
        roles,
        permissions,
        selectedWorkspace,
        setSelectedWorkspace: (workspace: string[]) => {
          // console.log(
          //   'Auth0Context: setSelectedWorkspace called with:',
          //   workspace,
          //   'type:',
          //   typeof workspace,
          //   'isArray:',
          //   Array.isArray(workspace)
          // );

          // Ensure we're always setting an array
          const workspaceToSet = Array.isArray(workspace) ? workspace : [workspace].filter(Boolean);
          console.log('auth0context: setting workspace to:', workspaceToSet);

          setSelectedWorkspaceState(workspaceToSet);

          // Update userData to ensure consistency
          setUserData((prevUserData) => ({
            ...prevUserData,
            organization: workspaceToSet[0] || null, // Use first workspace as primary
          }));
        },
        selectedGroup, // Add selectedGroup back as mirror of selectedWorkspace
        availableWorkspaces,
        setAvailableWorkspaces,
        login,
        logout,
        getUser,
        getAccessToken,
        updateToken,
        tokenExpiringSoon,
        getPermissions,
        // Update setupUserOrg to work with workspace array
        setupUserOrg: (workspace: string[]) => {
          // console.log(
          //   'Auth0Context: setupUserOrg called with workspace:',
          //   workspace,
          //   'type:',
          //   typeof workspace,
          //   'isArray:',
          //   Array.isArray(workspace)
          // );

          // Ensure we're always setting an array
          const workspaceToSet = Array.isArray(workspace) ? workspace : [workspace].filter(Boolean);
          //console.log('Auth0Context: setupUserOrg normalized workspace:', workspaceToSet);

          setSelectedWorkspaceState(workspaceToSet);
        },
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};
